import React, {useState, useEffect, useRef} from 'react';
import {Alert, Layout, Card, Col, Row, Space, Menu, Button, message, Dropdown, List, Avatar, Timeline, Modal, Form, Input, Tabs, Table, App} from 'antd';
import TeamShirt from '../../components/TeamShirt/TeamShirt';
import {ClockCircleFilled, CloseCircleFilled, DeleteOutlined, MedicineBoxOutlined, HomeOutlined, EditOutlined, PhoneOutlined, CreditCardOutlined, DownOutlined, MailOutlined, SettingFilled} from '@ant-design/icons';

// REDUX
import {useSelector, useDispatch} from 'react-redux';
import {getUser} from '../../actions/user';

import './Session.less';

// Libraries
import {FacebookProvider, SendToMessenger} from 'react-facebook';
import axios from 'axios';

import moment from 'moment';
// @ts-ignore
import 'moment/locale/is';

// Modals
import EditPlayerModal from './modals/EditPlayerModal';
import DivideIntoTeamsModal from './modals/DivideIntoTeamsModal';
import SendSessionReportModal from './modals/SendSessionReportModal';
import EditSessionModal from './modals/EditSessionModal';

// Components
import ChatBox from '../../components/ChatBox/ChatBox';

const {Content} = Layout;
const {TabPane} = Tabs;

// --- Types (simplified, adjust as needed) ---
interface User {
    id: number;
    signature_name: string;
    image?: {image: string};
    fb_recipient_id?: string;
}

interface Player {
    id: number;
    uuid: string;
    user?: User;
    unconfirmed_name?: string;
}

interface Team {
    uuid: string;
    name: string;
    players: Player[];
    substitutes: Player[];
    captains: Player[];
    main_kit_color?: string;
    main_kit_secondary_color?: string;
    main_kit_template?: string;
    away_kit_color?: string;
    away_kit_secondary_color?: string;
    away_kit_template?: string;
    description?: string;
    in_need_buffer?: number;
    emergency_buffer?: number;
}

interface Event {
    uuid: string;
    team: Team;
    title?: string;
    location?: any;
    category?: {icon?: string};
    ideal_player_count?: number;
}

interface SessionData {
    id: number;
    uuid: string;
    event: Event;
    confirmed: Player[];
    cant_make_it: Player[];
    waiting_list: Player[];
    is_cancelled?: boolean;
    date?: string;
}

interface Log {
    slug: string;
    created: string;
    user?: User;
    player: Player;
}

interface Notification {
    created: string;
    content: string;
    sms_messages: {player: string}[];
}

interface RootState {
    user: {user: User};
}

// Wrapper component to provide message API
const SessionWrapper = (props: any) => {
    const {message: messageApi} = App.useApp();
    return <Session {...props} messageApi={messageApi} />;
};

const Session: React.FC<{messageApi: any}> = ({messageApi}) => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.user.user);

    // State
    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState<SessionData | any>({});
    const [playersRefresh, setPlayersRefresh] = useState(false);
    const [waitingListModal, setWaitingListModal] = useState(false);
    const [fillSlotModal, setFillSlotModal] = useState(false);
    const [emergency, setEmergency] = useState(false);
    const [inNeed, setInNeed] = useState(false);
    const [isInTeam, setIsInTeam] = useState(false);
    const [isSubstitute, setIsSubstitute] = useState(false);
    const [logs, setLogs] = useState<Log[]>([]);
    const [sentNotifications, setSentNotifications] = useState<Notification[]>([]);
    const [payForUserModal, setPayForUserModal] = useState(false);
    const [divideIntoTeamsModal, setDivideIntoTeamsModal] = useState(false);
    const [sendReportModal, setSendReportModal] = useState<SessionData | null>(null);
    const [editSessionModal, setEditSessionModal] = useState<SessionData | null>(null);
    const [editPlayerModal, setEditPlayerModal] = useState(false);
    const [editPlayer, setEditPlayer] = useState<Player | null>(null);
    const [aboutEmergencyModal, setAboutEmergencyModal] = useState(false);
    const [addFriendToListModal, setAddFriendToListModal] = useState<false | string>(false);
    const [newFriendLoading, setNewFriendLoading] = useState(false);

    const friendFormRef = useRef<any>(null);

    useEffect(() => {
        dispatch(getUser() as any);

        axios
            .get('/api/session/' + (window as any).uuid + '/')
            .then((rsp) => {
                setSession(rsp.data);
                setIsInTeam(rsp.data.event?.team?.players.some((player: Player) => player.user?.id === (window as any).me));
                setIsSubstitute(rsp.data.event?.team?.substitutes.some((player: Player) => player.user?.id === (window as any).me));
                setLoading(false);

                axios.get('/api/log/?ordering=-created&category=Session&instance_id=' + rsp.data.id).then((rsp) => {
                    setLogs(rsp.data.results);
                });
            })
            .catch(() => {
                messageApi.error('Úps, Það finnst enginn tími með þessu númeri :(');
            });

        axios.get('/api/message/?session__uuid=' + (window as any).uuid).then((rsp) => {
            setSentNotifications(rsp.data.results);
        });
    }, [dispatch, messageApi]);

    // --- Helper functions ---
    const isCaptain = (player: Player) => session?.event?.team?.captains.filter((pl: Player) => pl.id === player.id).length;

    const filterOgPlayersInList = (list: Player[] = []) => {
        const players = session?.event?.team?.players || [];
        return list.filter((player) => players.some((pl: Player) => player.id === pl.id));
    };

    const isOgPlayer = (player: Player) => !!session?.event?.team?.players.some((pl: Player) => pl.id === player.id);

    const isSubstitutePlayer = (player: Player) => !!session?.event?.team?.substitutes.some((pl: Player) => pl.id === player.id);

    const inNeedEmptySeats = () => {
        const ideal = session?.event?.ideal_player_count || 0;
        const confirmed = session?.confirmed?.length || 0;
        return ideal - confirmed;
    };

    const notInNeedEmptySeats = () => {
        const playerCount = session?.event?.team?.players?.length || 0;
        const ogCantMakeItCount = filterOgPlayersInList(session?.cant_make_it).length;
        const ideal = session?.event?.ideal_player_count || 0;
        const confirmed = session?.confirmed?.length || 0;
        const ogConfirmedCount = filterOgPlayersInList(session?.confirmed).length;
        const playersNeeded = ideal - confirmed;
        const yetToAnswerCount = playerCount - ogCantMakeItCount - ogConfirmedCount;
        return playersNeeded - yetToAnswerCount;
    };

    const emptySeats = () => {
        if (!session?.confirmed) return 0;
        const ideal = session?.event?.ideal_player_count || 0;
        const confirmed = session?.confirmed?.length || 0;
        if (ideal <= confirmed) return 0;
        const empty = inNeed ? inNeedEmptySeats() : notInNeedEmptySeats();
        return empty > 0 ? empty : 0;
    };

    const sessionIsFull = () => (session?.confirmed?.length || 0) >= (session?.event?.ideal_player_count || 0);

    const playerAnswerCount = () => {
        if (!session?.confirmed) return 0;
        return [...(session?.confirmed || []), ...(session?.cant_make_it || [])].filter((player: Player) => {
            return (session?.event?.team?.players || []).map((team_player: Player) => team_player.id === player.id);
        }).length;
    };

    const removePlayerFromList = (player: Player, list_name: string) => {
        let newSession = {...session};
        newSession[list_name] = [...(session[list_name] || []).filter((pl: Player) => player.id !== pl.id)];

        axios
            .post(
                '/api/add_remove_player_to_session_list/',
                JSON.stringify({
                    player: player.uuid,
                    session: session.uuid,
                    list_name: list_name,
                    action: 'remove',
                })
            )
            .then(() => {
                messageApi.success('Tókst!');
                setSession(newSession);
                if (list_name === 'waiting_list') {
                    setLogs([{slug: 'player_removed_from_waiting_list', player} as Log, ...logs]);
                }
            })
            .catch((err) => {
                (window as any).test = err;
                if (err.response?.data?.message) {
                    messageApi.error(err.response?.data?.message);
                } else {
                    messageApi.error('Úps, eitthvað fór úrskeiðis!');
                }
            });
    };

    const addPlayerToList = (player: Player, list_name: string) => {
        axios
            .post(
                '/api/add_remove_player_to_session_list/',
                JSON.stringify({
                    player: player.uuid,
                    session: session.uuid,
                    list_name: list_name,
                })
            )
            .then(() => {
                messageApi.success('Tókst!');

                // Update session state with functional update
                setSession((prevSession) => {
                    const state_list = prevSession[list_name] || [];
                    const newSession = {...prevSession};
                    newSession[list_name] = [...state_list, player];

                    // Remove from other lists
                    let remove_from_lists: string[] = [];
                    if (list_name === 'confirmed') {
                        remove_from_lists.push('cant_make_it');
                        remove_from_lists.push('waiting_list');
                    }
                    if (list_name === 'cant_make_it') {
                        remove_from_lists.push('confirmed');
                    }
                    if (list_name === 'unsure') {
                        remove_from_lists.push('confirmed');
                        remove_from_lists.push('cant_make_it');
                    }
                    if (list_name === 'waiting_list') {
                        remove_from_lists.push('confirmed');
                        remove_from_lists.push('cant_make_it');
                    }

                    remove_from_lists.forEach((rival_list_name) => {
                        newSession[rival_list_name] = [...(newSession[rival_list_name] || []).filter((list_player: Player) => player.id !== list_player.id)];
                    });

                    return newSession;
                });

                // Update logs
                axios.get('/api/log/?ordering=-created&category=Session&instance_id=' + session.id).then((rsp) => {
                    setLogs(rsp.data.results);
                });
            })
            .catch((err) => {
                (window as any).test = err;
                if (err.response?.data?.message) {
                    console.log('!!!!', err.response?.data?.message);
                    messageApi.error(err.response?.data?.message);
                } else {
                    messageApi.error('Úps, eitthvað fór úrskeiðis!');
                }
            });
    };

    const submitFriend = (data: any) => {
        setNewFriendLoading(true);
        const email = data.unconfirmed_email || '';
        let phone = data.unconfirmed_phone || '';
        const name = data.unconfirmed_name;
        if (phone) phone = encodeURIComponent('+354' + phone);
        axios
            .get('/api/get_or_create_player/?email=' + email + '&phone=' + phone + '&name=' + name)
            .then((rsp) => {
                addPlayerToList(rsp.data, addFriendToListModal as string);
                setNewFriendLoading(false);
                friendFormRef.current.setFieldsValue({
                    unconfirmed_name: null,
                    unconfirmed_email: null,
                    unconfirmed_phone: null,
                });
            })
            .catch((err) => {
                setNewFriendLoading(false);
                if (err.response?.status === 403) {
                    messageApi.error('Þú hefur ekki leyfi til að framkvæma þessa aðgerð!');
                } else {
                    messageApi.error('Úps eitthvað fór úrskeiðis, ekki tókst að bæta leikmanni við :(');
                }
            });
    };

    // The render logic will be ported in the next chunk
    const location = session?.location || session?.event?.location;
    const categoryIcon = session?.event?.category?.icon;

    return (
        <>
            <Layout id='session'>
                <Content>
                    <Row gutter={[10, 10]} style={{margin: 10, justifyContent: 'center'}}>
                        <Col xs={24} sm={24} md={12} lg={8} xl={9} xxl={7}>
                            <Space direction='vertical' style={{display: 'flex'}}>
                                {session?.confirmed && session?.confirmed.length > 2 && (
                                    <Space direction='vertical' style={{display: 'flex'}}>
                                        <Button
                                            onClick={() => {
                                                if ((window as any).my_plan === 'FREE') {
                                                    setPayForUserModal(true);
                                                } else {
                                                    setDivideIntoTeamsModal(true);
                                                }
                                            }}
                                            block
                                            type='dashed'
                                            style={{background: 'transparent', color: '#fff'}}
                                            size='large'
                                        >
                                            Skipta í lið
                                        </Button>
                                    </Space>
                                )}
                                <Card
                                    title={
                                        <>
                                            <a style={{color: 'rgba(0, 0, 0, 0.85)'}} href={'/lid/' + session?.event?.team?.uuid + '/'}>
                                                <Button size='small'>Skoða lið</Button> {session?.event?.team?.name || ''}
                                            </a>
                                        </>
                                    }
                                    extra={
                                        isInTeam ? (
                                            <Dropdown
                                                overlay={
                                                    <Menu>
                                                        {!session.is_cancelled && (
                                                            <Menu.Item
                                                                key='1'
                                                                icon={<CreditCardOutlined />}
                                                                onClick={() => {
                                                                    setEditSessionModal(session);
                                                                }}
                                                            >
                                                                Breyta viðburði
                                                            </Menu.Item>
                                                        )}
                                                        {!session.is_cancelled && (
                                                            <Menu.Item
                                                                onClick={() => {
                                                                    setSendReportModal(session);
                                                                }}
                                                            >
                                                                <MailOutlined /> Senda liðaskýrslu
                                                            </Menu.Item>
                                                        )}
                                                        {!session.is_cancelled && (
                                                            <Menu.Item
                                                                danger
                                                                onClick={() => {
                                                                    axios.patch(`/api/session/${(window as any).uuid}/update/`, {is_cancelled: true}).then(() => {
                                                                        messageApi.success('Tókst');
                                                                        setSession({...session, is_cancelled: true});
                                                                    });
                                                                }}
                                                            >
                                                                <DeleteOutlined /> Fella viðburð niður
                                                            </Menu.Item>
                                                        )}
                                                        {session.is_cancelled && (
                                                            <Menu.Item
                                                                onClick={() => {
                                                                    axios.patch(`/api/session/${(window as any).uuid}/update/`, {is_cancelled: false}).then(() => {
                                                                        messageApi.success('Tókst');
                                                                        setSession({...session, is_cancelled: false});
                                                                    });
                                                                }}
                                                            >
                                                                <MedicineBoxOutlined /> Endurvekja viðburð
                                                            </Menu.Item>
                                                        )}
                                                    </Menu>
                                                }
                                            >
                                                <Button>
                                                    <SettingFilled /> Aðgerðir <DownOutlined />
                                                </Button>
                                            </Dropdown>
                                        ) : (
                                            <></>
                                        )
                                    }
                                    loading={loading}
                                    type='inner'
                                >
                                    <div id='field'>
                                        {session.is_cancelled ? (
                                            <div className='text-center'>
                                                <h1>Viðburður felldur niður</h1>
                                                <p>Það er því miður búið að fella þennan viðburð niður.</p>
                                            </div>
                                        ) : (
                                            <>
                                                {session.date && (
                                                    <div className='text-center' style={{fontSize: 17, borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: 10, marginBottom: 10}}>
                                                        <span style={{marginRight: 10}}>
                                                            Staðfestir: <strong className='successColor'>{session.confirmed.length}</strong>
                                                        </span>
                                                        {!sessionIsFull() ? (
                                                            <span>
                                                                {' '}
                                                                Laus pláss: <strong className='warningColor'> {session.event.ideal_player_count - session.confirmed.length}</strong>
                                                            </span>
                                                        ) : (
                                                            <strong className='successColor'> Fullbókað!</strong>
                                                        )}
                                                    </div>
                                                )}
                                                <div className='team'>
                                                    {session.confirmed?.map((player, i) => {
                                                        const is_og = isOgPlayer(player);

                                                        return (
                                                            <div key={i} className='player'>
                                                                <div style={{display: 'inline-block'}}>
                                                                    <TeamShirt
                                                                        className='shirt'
                                                                        kit_color={is_og ? session?.event?.team?.main_kit_color : session?.event?.team?.away_kit_color}
                                                                        kit_secondary_color={is_og ? session?.event?.team?.main_kit_secondary_color : session?.event?.team?.away_kit_secondary_color}
                                                                        kit_template={is_og ? session?.event?.team?.main_kit_template : session?.event?.team?.away_kit_template}
                                                                    ></TeamShirt>
                                                                </div>
                                                                <div className='playerNameContainer'>
                                                                    <div className='playerNameAndStatus'>
                                                                        {(isInTeam || (window as any).me === player.user?.id) && (
                                                                            <Button
                                                                                type='link'
                                                                                onClick={() => {
                                                                                    setEditPlayerModal(true);
                                                                                    setEditPlayer(player);
                                                                                }}
                                                                                style={{position: 'absolute', right: 0, top: 0, cursor: 'pointer', color: '#787878'}}
                                                                            >
                                                                                <EditOutlined></EditOutlined>
                                                                            </Button>
                                                                        )}
                                                                        <a style={{color: 'inherit'}} href={'/leikmadur/' + player.uuid + '/'} className='playerName capitalize'>
                                                                            {player.user?.signature_name || player.unconfirmed_name}
                                                                        </a>
                                                                        <div>
                                                                            <span role='img' aria-label='Strong'>
                                                                                💪
                                                                            </span>{' '}
                                                                            Mætir
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}

                                                    {session.event?.team?.players
                                                        ?.filter((player) => {
                                                            return ![...session.confirmed, ...session.cant_make_it]
                                                                .map((pl) => {
                                                                    return pl.id;
                                                                })
                                                                .includes(player.id);
                                                        })
                                                        .sort((player, other_player) => {
                                                            if (player.user?.id === (window as any).me) {
                                                                return -1;
                                                            } else {
                                                                if (isCaptain(player) && other_player.user?.id !== (window as any).me) return -1;

                                                                return 0;
                                                            }
                                                        })
                                                        .map((player, i) => {
                                                            return (
                                                                !sessionIsFull() &&
                                                                !emergency &&
                                                                !inNeed && (
                                                                    <div key={i} className='player'>
                                                                        {
                                                                            <div className='confirmationButtons'>
                                                                                <Space direction='vertical'>
                                                                                    <Button block onClick={() => addPlayerToList(player, 'confirmed')} type='primary'>
                                                                                        Mætir
                                                                                    </Button>
                                                                                    <Button block onClick={() => addPlayerToList(player, 'cant_make_it')} danger>
                                                                                        Mætir ekki
                                                                                    </Button>
                                                                                </Space>
                                                                            </div>
                                                                        }
                                                                        <div style={{display: 'inline-block', position: 'relative'}}>
                                                                            <TeamShirt
                                                                                style={{opacity: 0.2}}
                                                                                className='shirt'
                                                                                kit_color={session?.event?.team?.main_kit_color}
                                                                                kit_secondary_color={session?.event?.team?.main_kit_secondary_color}
                                                                                kit_template={session?.event?.team?.main_kit_template}
                                                                            ></TeamShirt>
                                                                        </div>
                                                                        <div className='playerNameContainer'>
                                                                            <div className='playerNameAndStatus'>
                                                                                <a style={{color: 'inherit'}} href={'/leikmadur/' + player.uuid + '/'} className='playerName capitalize'>
                                                                                    {player.user?.signature_name || player.unconfirmed_name}
                                                                                </a>
                                                                                <div>Á eftir að svara</div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )
                                                            );
                                                        })}

                                                    {session.confirmed &&
                                                        [...Array(emptySeats())].map((seat, i) => {
                                                            return (
                                                                <div key={i} className='player'>
                                                                    <div className='confirmationButtons'>
                                                                        <Space direction='vertical'>
                                                                            <br></br>
                                                                            {isInTeam || isSubstitute ? (
                                                                                <Button
                                                                                    onClick={() => {
                                                                                        setFillSlotModal(true);
                                                                                    }}
                                                                                    block
                                                                                    type='primary'
                                                                                >
                                                                                    Fylla pláss
                                                                                </Button>
                                                                            ) : (
                                                                                <Button
                                                                                    block
                                                                                    type='primary'
                                                                                    onClick={() => {
                                                                                        setWaitingListModal(true);
                                                                                    }}
                                                                                >
                                                                                    Sækja um
                                                                                </Button>
                                                                            )}
                                                                        </Space>
                                                                    </div>
                                                                    <div style={{display: 'inline-block'}}>
                                                                        <TeamShirt style={{opacity: 0.5}} className='shirt' kit_template={session.event?.team?.main_kit_template}></TeamShirt>
                                                                    </div>
                                                                    <div className='playerNameContainer'>
                                                                        <div className='playerNameAndStatus'>
                                                                            <div className='playerName'>Pláss laust</div>
                                                                            <div>🪑</div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </Card>

                                {!!!session.is_cancelled && (
                                    <Card
                                        title='Biðlistinn'
                                        loading={loading}
                                        type='inner'
                                        extra={
                                            <Button
                                                type='primary'
                                                onClick={() => {
                                                    setWaitingListModal(true);
                                                }}
                                            >
                                                Skrá á biðlista
                                            </Button>
                                        }
                                    >
                                        {!!session.waiting_list?.length && (
                                            <div id='field'>
                                                <div className='team'>
                                                    {session.waiting_list?.map((player) => {
                                                        const is_substitute = !!session.event?.team?.substitutes.filter((substitute) => {
                                                            return substitute.id === player.id;
                                                        }).length;
                                                        return (
                                                            <div key={player.id} className='player'>
                                                                <TeamShirt
                                                                    className='shirt'
                                                                    kit_color={is_substitute ? session.event?.team?.away_kit_color : false}
                                                                    kit_secondary_color={is_substitute ? session.event?.team?.away_kit_secondary_color : false}
                                                                    kit_template={session?.event?.team?.away_kit_template}
                                                                ></TeamShirt>
                                                                <div className='playerNameContainer'>
                                                                    {is_substitute && <div className='playerStatus'>Með forgang (varamaður)</div>}
                                                                    <div className='playerNameAndStatus'>
                                                                        {(isInTeam || (window as any).me === player.user?.id) && (
                                                                            <div onClick={() => removePlayerFromList(player, 'waiting_list')} style={{position: 'absolute', right: 4, top: 0, cursor: 'pointer'}}>
                                                                                <CloseCircleFilled style={{color: '#f74d4f'}} />
                                                                            </div>
                                                                        )}

                                                                        <a className='playerName capitalize' href={'/leikmadur/' + player.uuid + '/'}>
                                                                            <div className='capitalize'>{player.user?.signature_name || player.unconfirmed_name}</div>
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {!session.waiting_list?.length && (
                                            <div className='text-center'>
                                                Biðlistinn er tómur
                                                <span role='img' aria-label='Grumpy'>
                                                    😒
                                                </span>
                                            </div>
                                        )}
                                    </Card>
                                )}
                            </Space>
                        </Col>

                        <Col xs={24} sm={24} md={12} lg={8} xl={9} xxl={7}>
                            <Card
                                styles={{
                                    body: {textAlign: 'center', padding: 0},
                                }}
                                className='location_box transparent_box'
                                loading={loading}
                            >
                                <h2>{session.event?.title || ''}</h2>
                                <div>
                                    <Avatar
                                        size={40}
                                        style={{color: '#e100fe', background: categoryIcon ? 'transparent' : '#fff'}}
                                        icon={!categoryIcon ? <HomeOutlined /> : null}
                                        src={location?.main_image || categoryIcon}
                                    >
                                        {location?.title}
                                    </Avatar>
                                </div>

                                <h3>{location?.title}</h3>
                                <h4>{location?.google_place_secondary_text}</h4>
                                <hr></hr>
                                <h3 className='mb0'>
                                    <ClockCircleFilled /> <span>{moment(session?.date).format('LT')}</span>
                                </h3>
                                <h4 className='mb0 mt0 weekday'>
                                    <span>{moment(session?.date).format('dddd')}</span>, <span>{moment(session?.date).format('LL')}</span>
                                </h4>

                                <p>{session?.event?.team?.description}</p>
                            </Card>

                            {session.uuid && (isInTeam || isSubstitute) && session.event?.team?.players && (
                                <Card
                                    styles={{
                                        title: {
                                            color: 'white',
                                        },
                                    }}
                                    style={{
                                        background: '#ffffff47',
                                        border: 'none',
                                    }}
                                    bodyStyle={{padding: 0}}
                                    title='Spjall'
                                    type='inner'
                                    loading={loading}
                                >
                                    <ChatBox players={session.event?.team?.players} session={session}></ChatBox>
                                </Card>
                            )}

                            <Space direction='vertical' className='text-center' style={{display: 'flex'}}>
                                {!sessionIsFull() && (
                                    <div style={{marginTop: 20}}>
                                        <Space direction='vertical' style={{display: 'flex'}}>
                                            {inNeed ? (
                                                <Alert message='Varamenn hafa núna sama forgang og leikmenn, fyrstur kemur fyrstur fær!' type='warning' />
                                            ) : (
                                                <div style={{color: 'white'}}>
                                                    Gul viðvörun:{' '}
                                                    <strong>
                                                        <span>{moment(session?.date).subtract(session.event?.team?.in_need_buffer, 'hours').format('LT')}</span>
                                                    </strong>
                                                </div>
                                            )}

                                            {emergency ? (
                                                <div style={{color: 'white'}}>
                                                    <h3>Nú er neyðarástand!</h3>
                                                    <div>Leikmenn geta bætt við fólki sem er hvorki í leikmannahópnum né á varamannabekknum</div>
                                                </div>
                                            ) : (
                                                <div style={{color: 'white'}}>
                                                    Rauð viðvörun:{' '}
                                                    <strong>
                                                        <span>{moment(session?.date).subtract(session.event?.team?.emergency_buffer, 'hours').format('LT')}</span>
                                                    </strong>
                                                </div>
                                            )}

                                            <Button
                                                onClick={() => {
                                                    setAboutEmergencyModal(true);
                                                }}
                                                type='link'
                                                block
                                                style={{color: '#fff', textDecoration: 'underline'}}
                                            >
                                                Hvað þýðir það?
                                            </Button>

                                            {!loading && (isInTeam || isSubstitute) && !user?.fb_recipient_id && (
                                                <>
                                                    <hr />
                                                    <h3>Fáðu tilkynningar á messenger</h3>
                                                    <div className='text-left' style={{width: 178, margin: 'auto'}}>
                                                        <FacebookProvider appId='238324500800347'>
                                                            <SendToMessenger messengerAppId='238324500800347' pageId='104971504676510' dataRef={(window as any).me} size='xlarge' color='white' />
                                                        </FacebookProvider>
                                                    </div>
                                                    <hr />
                                                </>
                                            )}
                                        </Space>
                                    </div>
                                )}

                                <Card loading={loading && !!logs} className='transparent_box'>
                                    <Tabs className='sessionTabs' style={{color: '#fff'}} defaultActiveKey='timeline' centered>
                                        <TabPane tab='Aðgerðasaga' key='timeline'>
                                            {logs?.length ? (
                                                <Timeline mode='right'>
                                                    {logs.map((log, key) => {
                                                        return (
                                                            <Timeline.Item
                                                                key={key}
                                                                color={log.slug === 'player_marked_confirmed' || log.slug === 'player_added_to_waiting_list' ? 'green' : 'red'}
                                                                label={
                                                                    <>
                                                                        <div>{moment(log.created).format('HH:mm - D. MMM')}</div>
                                                                        <div style={{fontSize: '9px'}}>{log.user?.signature_name}</div>
                                                                    </>
                                                                }
                                                            >
                                                                <a style={{color: '#fff'}} href={'/leikmadur/' + log.player.uuid}>
                                                                    <strong className='capitalize'>{log.player.user?.signature_name || log.player.unconfirmed_name} </strong>
                                                                    <Avatar size='small' src={log.player.user?.image?.image}>
                                                                        {(log.player.user?.signature_name || log.player.unconfirmed_name || '').charAt(0).toUpperCase()}
                                                                    </Avatar>
                                                                </a>
                                                                {log.slug === 'player_marked_confirmed' && ' mætir!'}
                                                                {log.slug === 'player_marked_unsure' && ' merktur sem "á eftir að svara"'}
                                                                {log.slug === 'player_marked_cant_make_it' && ' mætir ekki.'}
                                                                {log.slug === 'player_added_to_waiting_list' && ' bætt við biðlistann.'}
                                                                {log.slug === 'player_removed_from_waiting_list' && ' fjarlægð/ur af biðlistanum.'}
                                                                {log.slug === 'player_marked_no_show' && ' merktur sem skrópari.'}
                                                                {log.slug === 'player_marked_late' && ' merktur sem "mætti seint".'}
                                                                {(isInTeam || log.player.user?.id === (window as any).me) && (
                                                                    <Button
                                                                        onClick={() => {
                                                                            setEditPlayer(log.player);
                                                                            setEditPlayerModal(true);
                                                                        }}
                                                                        size='small'
                                                                        type='link'
                                                                        style={{background: 'transparent', color: '#fff', border: 'none'}}
                                                                    >
                                                                        <EditOutlined />
                                                                    </Button>
                                                                )}
                                                            </Timeline.Item>
                                                        );
                                                    })}
                                                </Timeline>
                                            ) : (
                                                <div>Sagan fyrir þennan viðburð er ansi tíðindalaus 🥱</div>
                                            )}
                                        </TabPane>

                                        <TabPane tab='Áminningar' key='notifications'>
                                            {isInTeam && (
                                                <div className='text-center'>
                                                    <Button>
                                                        <a href={'/aminningar/' + session.event?.uuid + '/'}>Stilla áminningar</a>
                                                    </Button>
                                                </div>
                                            )}

                                            <Space style={{width: '100%'}} direction='vertical'>
                                                {sentNotifications?.length ? (
                                                    sentNotifications.map((notification, key) => {
                                                        return (
                                                            <Card
                                                                key={key}
                                                                className='sms_info'
                                                                style={{background: 'transparent', color: '#fff', width: '100%'}}
                                                                headStyle={{textAlign: 'left', color: '#fff'}}
                                                                title={
                                                                    <>
                                                                        <PhoneOutlined /> SMS áminning
                                                                    </>
                                                                }
                                                                size='small'
                                                                extra={moment(notification.created).format('HH:mm - D. MMM')}
                                                            >
                                                                <div>{notification.content}</div>
                                                                <br />
                                                                <h3>Viðtakendur ({notification.sms_messages.length})</h3>

                                                                <Table
                                                                    pagination={false}
                                                                    showHeader={false}
                                                                    size='small'
                                                                    dataSource={notification.sms_messages.map((sms, key) => ({
                                                                        ...sms,
                                                                        key: key,
                                                                    }))}
                                                                    locale={{
                                                                        emptyText: 'Viðtakendalisti fannst ekki',
                                                                    }}
                                                                    columns={[
                                                                        {
                                                                            title: 'Móttakandi',
                                                                            key: 'key',
                                                                            render: (res) => res.player || 'Viðtakandi fannst ekki',
                                                                        },
                                                                    ]}
                                                                ></Table>
                                                            </Card>
                                                        );
                                                    })
                                                ) : (
                                                    <div>Engar áminningar hafa verið sendar fyrir þennan viðburð</div>
                                                )}
                                            </Space>
                                        </TabPane>
                                    </Tabs>
                                </Card>
                            </Space>
                        </Col>
                    </Row>
                </Content>
            </Layout>

            {/* Modals */}
            <Modal
                visible={waitingListModal}
                onCancel={() => {
                    setWaitingListModal(false);
                }}
                footer={
                    <Space style={{display: 'flex'}} direction='vertical'>
                        {session.confirmed &&
                            !isInTeam &&
                            (window as any).me &&
                            (![...(session.event?.team?.players || []), ...(session?.confirmed || []), ...(session?.waiting_list || [])].filter((player) => player.user?.id === (window as any).me).length ? (
                                <Button
                                    type='primary'
                                    size='large'
                                    block
                                    onClick={() => {
                                        axios.get('/api/get_or_create_player/?user=' + (window as any).me).then((rsp) => {
                                            addPlayerToList(rsp.data, 'waiting_list');
                                        });
                                    }}
                                >
                                    Skrá mig á biðlistann
                                </Button>
                            ) : (
                                <h3 className='text-center'>Þú ert komin/n biðlistann!</h3>
                            ))}
                        {isInTeam && (
                            <div>
                                <Button
                                    onClick={() => {
                                        setAddFriendToListModal('waiting_list');
                                        setNewFriendLoading(false);
                                        setWaitingListModal(false);
                                    }}
                                    type='primary'
                                    size='large'
                                    block
                                >
                                    Skrá vin á biðlistann
                                </Button>

                                <h3 className='text-center mt20'>eða bæta varamanni á biðlista:</h3>
                                <br />
                                <List
                                    className='text-left'
                                    locale={{emptyText: 'Það eru engir varamenn til að sýna'}}
                                    itemLayout='horizontal'
                                    dataSource={session.event?.team?.substitutes}
                                    renderItem={(player: Player) => (
                                        <List.Item
                                            actions={[
                                                <Button onClick={() => addPlayerToList(player, 'waiting_list')} type='primary'>
                                                    Skrá á biðlista
                                                </Button>,
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={
                                                    <TeamShirt
                                                        style={{width: 35}}
                                                        className='shirt'
                                                        kit_color={session?.event?.team?.away_kit_color}
                                                        kit_secondary_color={session?.event?.team?.away_kit_secondary_color}
                                                        kit_template={session?.event?.team?.away_kit_template}
                                                    ></TeamShirt>
                                                }
                                                title={<div className='capitalize'>{player.user?.signature_name || player.unconfirmed_name}</div>}
                                            ></List.Item.Meta>
                                        </List.Item>
                                    )}
                                />
                            </div>
                        )}

                        {!(window as any).me && (
                            <div className='text-center'>
                                <h3>Þú verður að vera innskráð/ur til að skrá þig á biðlistann!</h3>
                                <Space>
                                    <Button size='large' block type='primary'>
                                        <a href='/nyskra/'>Búa til aðgang</a>
                                    </Button>

                                    <Button size='large' block type='dashed'>
                                        <a href='/innskra/'>Innskrá</a>
                                    </Button>
                                </Space>
                            </div>
                        )}
                    </Space>
                }
            ></Modal>

            {aboutEmergencyModal && (
                <Modal
                    onCancel={() => {
                        setAboutEmergencyModal(false);
                    }}
                    visible={aboutEmergencyModal}
                    footer={
                        <div className='text-left'>
                            <h2 className='mb0'>Gul viðvörun</h2>
                            <h4>
                                Skellur á {session?.event?.team?.in_need_buffer} tímum fyrir viðburðinn ({session && moment(session?.date).subtract(session?.event?.team?.in_need_buffer, 'hours').format('LT')})
                            </h4>
                            <p>
                                Mikilvægt er að leikmenn í aðal hópnum meldi sig áður en gul viðvörun skellur á. Á þessum tímapunkti fá <strong>varamenn</strong> sama forgang og leikmenn og geta skráð sig í laus sæti.
                            </p>

                            <h2 className='mb0'>Rauð viðvörun</h2>
                            <h4>
                                Skellur á {session?.event?.team?.emergency_buffer} tímum fyrir viðburðinn ({session && moment(session?.date).subtract(session?.event?.team?.emergency_buffer, 'hours').format('LT')})
                            </h4>
                            <p>Þegar rauð viðvörun skellur á geta leikmenn bætt ókunnugum í laus sæti.</p>
                            <p>
                                Athugið að <strong>ókunnugir geta EKKI gert það sjálfir</strong>, þeir geta einungis bætt sér á biðlistann. Einungis leikmenn geta bætt ókunnugum af biðlistanum í laus sæti.
                            </p>
                        </div>
                    }
                ></Modal>
            )}

            <Modal
                visible={!!addFriendToListModal}
                title='Bæta vin á biðlistann'
                onCancel={() => {
                    setAddFriendToListModal(false);
                }}
                footer={
                    <Space style={{display: 'flex'}} direction='vertical'>
                        {session.confirmed && isInTeam && (
                            <div className='text-center'>
                                <p>Við mælum mjög sterklega með því að þú fyllir út símanúmer og/eða netfang leikmanns, það er þó ekki nauðsynlegt. </p>
                                <Form ref={friendFormRef} onFinish={submitFriend} layout='vertical' name='basic'>
                                    <Form.Item
                                        label='Nafn'
                                        name='unconfirmed_name'
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Nafn er ekki valkvætt!',
                                            },
                                        ]}
                                    >
                                        <Input size='large' autoComplete='new-password' />
                                    </Form.Item>

                                    <Form.Item label='Netfang vinar' name='unconfirmed_email'>
                                        <Input size='large' autoComplete='new-password' />
                                    </Form.Item>

                                    <Form.Item
                                        label='Símanúmer vinar'
                                        name='unconfirmed_phone'
                                        rules={[
                                            {
                                                validator: (rule, value) => {
                                                    return !value || value.length === 7 ? Promise.resolve() : Promise.reject('Símanúmerið þarf að vera 7 stafir');
                                                },
                                            },
                                        ]}
                                    >
                                        <Input size='large' autoComplete='new-password' />
                                    </Form.Item>

                                    <Form.Item>
                                        <Button block size='large' loading={newFriendLoading} type='primary' htmlType='submit'>
                                            Vista
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </div>
                        )}
                    </Space>
                }
            ></Modal>

            <Modal
                visible={fillSlotModal}
                onCancel={() => {
                    setFillSlotModal(false);
                }}
                title='Veldu leikmann'
                footer={false}
            >
                <Space style={{display: 'flex'}} direction='vertical'>
                    <div className='text-center'>
                        <Button
                            onClick={() => {
                                setAddFriendToListModal('confirmed');
                                setNewFriendLoading(false);
                                setWaitingListModal(false);
                                setFillSlotModal(false);
                            }}
                        >
                            + Bæta við ókunnugum
                        </Button>
                    </div>

                    <h3 className='text-center'>Biðlistinn</h3>
                    <List
                        className='text-left'
                        locale={{emptyText: 'Biðlistinn er tómur :/'}}
                        itemLayout='horizontal'
                        dataSource={session?.waiting_list}
                        renderItem={(player: Player) => (
                            <List.Item
                                actions={[
                                    <Button
                                        onClick={() => {
                                            addPlayerToList(player, 'confirmed');
                                            setFillSlotModal(false);
                                        }}
                                        type='primary'
                                    >
                                        Bæta við viðburð
                                    </Button>,
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <TeamShirt
                                            style={{width: 35}}
                                            className='shirt'
                                            kit_color={session?.event?.team?.away_kit_color}
                                            kit_secondary_color={session?.event?.team?.away_kit_secondary_color}
                                            kit_template={session?.event?.team?.away_kit_template}
                                        ></TeamShirt>
                                    }
                                    title={<div className='capitalize'>{player.user?.signature_name || player.unconfirmed_name}</div>}
                                ></List.Item.Meta>
                            </List.Item>
                        )}
                    />

                    <h3 className='text-center'>Varamannabekkurinn</h3>
                    <List
                        className='text-left'
                        locale={{emptyText: 'Varamannabekurinn er tómur :/'}}
                        itemLayout='horizontal'
                        dataSource={session?.event?.team?.substitutes}
                        renderItem={(player: Player) => {
                            const waiting_list = !!session.waiting_list.filter((pl) => pl.id === player.id).length;
                            const confirmed_list = !!session.confirmed.filter((pl) => pl.id === player.id).length;

                            if (!waiting_list && !confirmed_list) {
                                return (
                                    <List.Item
                                        actions={[
                                            <Button
                                                onClick={() => {
                                                    addPlayerToList(player, 'confirmed');
                                                    setFillSlotModal(false);
                                                }}
                                                type='primary'
                                            >
                                                {'Bæta við viðburð'}
                                            </Button>,
                                        ]}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <TeamShirt
                                                    style={{width: 35}}
                                                    className='shirt'
                                                    kit_color={session?.event?.team?.away_kit_color}
                                                    kit_secondary_color={session?.event?.team?.away_kit_secondary_color}
                                                    kit_template={session?.event?.team?.away_kit_template}
                                                ></TeamShirt>
                                            }
                                            title={
                                                <div>
                                                    <div className='capitalize'>{player.user?.signature_name || player.unconfirmed_name}</div>
                                                </div>
                                            }
                                        ></List.Item.Meta>
                                    </List.Item>
                                );
                            }
                        }}
                    />

                    <h3 className='text-center'>Leikmannalistinn</h3>
                    <List
                        className='text-left'
                        locale={{emptyText: 'Það eru engir leikmenn til að sýna'}}
                        itemLayout='horizontal'
                        dataSource={session?.event?.team?.players}
                        renderItem={(player: Player) => {
                            const cant_make_it = !!session.cant_make_it.filter((pl) => pl.id === player.id).length;

                            return (
                                !!!session.confirmed.filter((pl) => pl.id === player.id).length && (
                                    <List.Item
                                        actions={[
                                            <Button
                                                onClick={() => {
                                                    addPlayerToList(player, 'confirmed');
                                                    setFillSlotModal(false);
                                                }}
                                                type='primary'
                                            >
                                                {cant_make_it ? 'Breyta í mætir' : 'Bæta við viðburð'}
                                            </Button>,
                                        ]}
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <TeamShirt
                                                    style={{width: 35}}
                                                    className='shirt'
                                                    kit_color={session?.event?.team?.main_kit_color}
                                                    kit_secondary_color={session?.event?.team?.main_kit_secondary_color}
                                                    kit_template={session?.event?.team?.main_kit_template}
                                                ></TeamShirt>
                                            }
                                            title={
                                                <div>
                                                    <div className='capitalize'>{player.user?.signature_name || player.unconfirmed_name}</div>
                                                    {cant_make_it && (
                                                        <div className='errorColor'>
                                                            <strong>Mætir ekki</strong>
                                                        </div>
                                                    )}
                                                </div>
                                            }
                                        ></List.Item.Meta>
                                    </List.Item>
                                )
                            );
                        }}
                    />
                </Space>
            </Modal>

            {divideIntoTeamsModal && session?.confirmed?.length && (
                <DivideIntoTeamsModal
                    team={session?.event?.team}
                    visible={divideIntoTeamsModal}
                    onCancel={() => {
                        setDivideIntoTeamsModal(false);
                    }}
                    players={session?.confirmed}
                ></DivideIntoTeamsModal>
            )}

            {sendReportModal && (
                <SendSessionReportModal
                    visible={!!sendReportModal}
                    session={sendReportModal}
                    onCancel={() => {
                        setSendReportModal(null);
                    }}
                ></SendSessionReportModal>
            )}

            {!!editSessionModal && (
                <EditSessionModal
                    visible={!!editSessionModal}
                    onCancel={() => {
                        setEditSessionModal(null);
                    }}
                    session={editSessionModal}
                    onSuccess={(data) => {
                        const new_data: any = {date: data.date};
                        if (data.location) new_data.location = data.location;

                        setEditSessionModal(null);
                        setSession({...session, ...new_data});
                    }}
                ></EditSessionModal>
            )}

            {editPlayer && (
                <EditPlayerModal
                    visible={editPlayerModal}
                    onCancel={() => {
                        setEditPlayerModal(false);
                        setEditPlayer(null);
                    }}
                    player={editPlayer}
                    onClick={(list_name) => {
                        addPlayerToList(editPlayer, list_name);
                        setEditPlayer(null);
                        setEditPlayerModal(false);
                    }}
                    team={session?.event?.team}
                    is_substitute={isSubstitutePlayer(editPlayer)}
                    is_og_player={isOgPlayer(editPlayer)}
                    title='Breyta mætingu'
                    defaultValue={
                        session.confirmed.some((player) => {
                            return editPlayer.id === player.id;
                        })
                            ? 'confirmed'
                            : 'cant_make_it'
                    }
                ></EditPlayerModal>
            )}
        </>
    );
};

export default SessionWrapper;
